// University of Dayton: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UD's Common Data Set 2025-2026, a Google Docs file linked from the
//    Office of Decision Support's "Data and Quick Facts" page (the SOURCES
//    url is that udayton.edu page; read through the file's text export). C1
//    applicants, admits and enrollees for Fall 2025 (the residency table
//    gives totals only, so no residency rows); C8 test policy for Fall 2027;
//    C21/C22 plans; G1/G5 2026-2027 costs. The file says its figures were
//    "Preliminary until July 2026".
//  - UD Admissions: Application and Admission Process (fall timeline, test
//    policy, review) and Nursing Admission. Neither page carries a year;
//    they are the pages for the application that opened August 1, 2026.
//    No change for fall 2028 entry was announced there.
//  - College Scorecard, school id 202480 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost: the CDS G1 items match the Tuition and Aid office's "Undergraduate
// Direct Costs for 2026-27" (tuition $51,910, first-year housing $10,140,
// food $7,050, health and wellbeing $600). G5 gives housing as $10,940 for
// residents, which does not match G1 or the aid office; the G1 figure is
// used. Books, transportation and other expenses are from G5.
// Quirk: the admission process page gives March 1 as the nursing deadline
// to confirm enrollment; the nursing page gives February 1. The nursing
// page's date is used, with the other noted.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'University of Dayton Common Data Set 2025-2026',
        url: 'https://udayton.edu/provost/ods/common_data_set.php',
        read: '2026-10-01',
    },
    process: {
        label: 'University of Dayton Admission, Application and Admission Process',
        url: 'https://udayton.edu/apply/undergraduate/admission-process.php',
        read: '2026-10-01',
    },
    nursing: {
        label: 'University of Dayton Admission, Nursing Admission',
        url: 'https://udayton.edu/apply/undergraduate/nursing.php',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?202480-University-of-Dayton',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-dayton',
    kbId: 'university_of_dayton',
    name: 'University of Dayton',
    shortName: 'Dayton',
    aliases: ['UD', 'UDayton'],
    location: 'Dayton, OH',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UD brand colors: Flyers Blue, Red
    // Scare); decoration only.
    branding: {
        primary: '#003087',
        secondary: '#CE0037',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_dayton_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 22247,
        admitted: 14814,
        enrolled: 1578,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UD\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'UD has no early decision plan. Its early action plan is not binding and not restrictive (November 1 closing date, December 1 notification in the Common Data Set), and the Common Data Set does not report how many applied or were admitted early, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to a major or program, and the admission committee may admit you to a different program than the one you chose. Nursing has its own process:',
        rows: [
            { school: 'Nursing (BSN)', requirement: 'A selective program with limited spaces, run with Sinclair Community College: you earn an associate degree in nursing and a BSN in four years. Apply by November 1 with a nursing application essay; notifications come in December, and admitted students confirm by February 1. Fall entry only. To move on to Sinclair\'s nursing program, students must score at least a 75% composite on the HESI A2 exam within three attempts.', source: 'nursing' },
        ],
        note: 'The general admission page gives March 1 as the nursing confirmation deadline; the nursing page says February 1.',
        source: 'process',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular admission', value: 'February 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'From November, then rolling', binding: false },
            { plan: 'Regular admission', deadline: 'February 1', decision: 'Rolling', binding: false },
        ],
        note: 'The application (Common App or UD\'s own, free, with an essay) opens August 1. Early action is not binding. UD starts releasing decisions to early action applicants in November and keeps sending decisions on a rolling basis. February 1 is also the priority deadline for the FAFSA, and May 1 is the deadline to confirm enrollment. You are considered for merit scholarships automatically when you apply.',
        source: 'process',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'UD is test-optional: send official ACT or SAT scores only if you want them considered. Scores are never the sole or primary reason for a decision; they are read alongside the rest of the application.',
        source: 'process',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by March 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'process',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 51910 },
            { item: 'Required fees (health and wellbeing)', amount: 600 },
            { item: 'Housing (on campus)', amount: 10140 },
            { item: 'Food (on-campus meal plan)', amount: 7050 },
            { item: 'Books and supplies', amount: 1000 },
            { item: 'Transportation', amount: 1210 },
            { item: 'Other expenses', amount: 2110 },
        ],
        total: 74020,
        note: 'For a full-time first-year student living on campus, fall and spring. The health and wellbeing fee is UD\'s only fee and applies to students starting in fall 2026 or later.',
        source: 'cds',
        where: 'sections G1, G3 and G5',
        netPrice: {
            average: 30366,
            byIncome: [
                { income: '$0 to $30,000', amount: 22720 },
                { income: '$30,001 to $48,000', amount: 20190 },
                { income: '$48,001 to $75,000', amount: 21370 },
                { income: '$75,001 to $110,000', amount: 26601 },
                { income: 'Over $110,000', amount: 36044 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UD\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
